import { Injectable, Inject, InjectionToken, Optional, NgZone, OnDestroy } from '@angular/core';
import { MatLegacySnackBarDismiss as MatSnackBarDismiss, MatLegacySnackBarRef as MatSnackBarRef, LegacyTextOnlySnackBar as TextOnlySnackBar } from '@angular/material/legacy-snack-bar';
import { find } from 'lodash-es';
import moment from 'moment';
import { EMPTY, from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ApNotificationService, ApSnackBarService } from 'webcommon/core-ui';
import {
  ApBaseClass,
  getFromBetween,
  MacroActionValueType,
  MacroContextEnum,
  MacroDataSourceEnum,
  MacroDefinitionDto,
  MacroDeleteResultDto,
  MacroExecutionDto,
  MacroExecutionResultDto,
  MacroGetDto,
  PatientInfoChangedEventArgs,
  PatientInfoItem,
} from 'webcommon/shared';

import { ApMacroDatePickerModalService } from '../components/ap-macro-date-picker-modal.service';
import { MacroActionValueDtoExtended } from '../MacroActionValueDtoExtended';
import { MacroConfigDto } from '../MacroConfigDto';
import { MacroInterfaces } from '../MacroInterfaces';
import { MacroDataService } from './MacroDataService';
import { MacroRepository } from './MacroRepository';
import { IMacroEventNotification } from '../IMacroEventNotification';
import { IMacroServiceDrugScreening } from '../IMacroServiceDrugScreening';

// Create InjectionTokens to allow the interfaces IMacroServiceDrugScreening and IMacroEventNotification to be injected.
// Use an InjectionToken whenever the type you are injecting is not reified(does not have a runtime representation)
// such as when injecting an interface, callable type, array or parameterized type.
//
// InjectionToken // https://www.tektutorialshub.com/angular/injection-token-in-angular/
// https://stackoverflow.com/questions/53317225/angular-injectiontoken-throws-no-provider-for-injectiontoken
export const IMacroServiceDrugScreeningToken = new InjectionToken<IMacroServiceDrugScreening>('IMacroServiceDrugScreening');
export const IMacroEventNotificationToken = new InjectionToken<IMacroEventNotification>('IMacroEventNotification');

@Injectable()
export class MacroService extends ApBaseClass implements OnDestroy {
  private macroDefinitions?: MacroDefinitionDto[];
  private macroListChangedSubject$ = new Subject<boolean>();
  activeSnackBarItem: MacroDefinitionDto | null = null;
  macroListChanged$ = this.macroListChangedSubject$.asObservable();
  snackBarRef: MatSnackBarRef<TextOnlySnackBar> | null = null;

  constructor(
    private macroRepository: MacroRepository,
    private macroDatePickerModalService: ApMacroDatePickerModalService,
    private macroDataService: MacroDataService,
    private apNotificationService: ApNotificationService,
    private ngZone: NgZone,
    public apSnackBarService: ApSnackBarService,
    @Optional() @Inject(IMacroServiceDrugScreeningToken) private drugScreeningService?: IMacroServiceDrugScreening,
    @Optional() @Inject(IMacroEventNotificationToken) private eventNotifications?: IMacroEventNotification,
  ) {
    super();
    this.subscribeToPatientInfoChanges();
  }

  override ngOnDestroy() {
    this.macroListChangedSubject$.complete();
    super.ngOnDestroy();
  }

  private subscribeToPatientInfoChanges() {
    if (!this.eventNotifications) return;
    this.eventNotifications.onPatientInfoChanged().pipe(
      this.takeUntilDestroy(),
    ).subscribe(
      (result) => {
        this.processPatientInfoChanged(result);
      },
      () => { /* do nothing */ },
    );
  }

  initialize(visitId: string | null, patientId: string): void {
    this.reset();
    this.macroDataService.initialize(visitId, patientId);

    if (patientId) {
      const macroGetDto = new MacroGetDto();
      macroGetDto.PatientId = patientId;
      macroGetDto.VisitId = visitId;

      this.macroRepository.getMacroList(patientId, macroGetDto).toPromise().then(
        (values) => {
          this.macroDefinitions = values;
          values.forEach((macro) => {
            macro.Description = macro.Description.replace(/\\n/g, '<br />');
          });
          this.sortMacroDefinitions();
          this.onMacroListHasChanged();
        },
        () => {
          this.onLoadError();
        },
      );
    }
  }

  private throwNotInitializedError(): never {
    throw new Error('MacroService not initialized');
  }

  getPatientId(): string {
    return this.macroDataService.getPatientId();
  }

  getVisitId(): string | null {
    return this.macroDataService.getVisitId();
  }

  getMacroList(): MacroDefinitionDto[] {
    return this.macroDefinitions || this.throwNotInitializedError();
  }

  // Add new macro definition (how will modified macros be handled?)
  // Should could simply reload>? Persistent values?
  addMacroDefinitionDto(macroDefinitionDto: MacroDefinitionDto): void {
    this.macroDefinitions?.push(macroDefinitionDto);
    this.sortMacroDefinitions();
    this.onMacroListHasChanged();
  }

  getContentByUXId(id: string, macroConfigDto: MacroConfigDto): Observable<MacroActionValueDtoExtended> {
    const item = this.macroDefinitions?.find((x) => x.TransientUXId === id);
    if (!item) {
      // unable to find macro, close auto completer
      const defaultValue: MacroActionValueDtoExtended = {
        Action: MacroActionValueType.Close,
        Value: '',
      };
      return of(defaultValue);
    }

    if (item.RequiresBackendProcessing && item.Id) {
      return this.doMacroBackendProcessing(item, macroConfigDto)
        .pipe(
          tap((result) => this.processMacroBackendResult(result)),
          switchMap(() => this.getValue(item, macroConfigDto)),
        );
    } else {
      return this.getValue(item, macroConfigDto);
    }
  }

  getMatchingResults(pattern: string, macroInterfaces: MacroInterfaces): MacroDefinitionDto[] {
    if (this.macroDefinitions) {
      let matched = this.macroDefinitions.filter((c) => {
        return (c.SupportedEditors.includes('any') || c.IsUserDefined === true)
          && c.Name?.toLowerCase().indexOf(pattern.toLowerCase()) === 0;
      });

      // add macros supported by tile to list
      if (macroInterfaces && macroInterfaces.supportedMacroInterfaceTypes) {
        const parentsupported = this.macroDefinitions.filter((x) => {
          return macroInterfaces.supportedMacroInterfaceTypes.findIndex((y) => x.Name === y.toString()) >= 0;
        }).filter((z) => z.Name?.indexOf(pattern) === 0);

        if (parentsupported.length > 0) {
          matched = matched.concat(parentsupported);
        }
      }

      if (matched.length > 0) {
        matched = matched.sort((x, y) => {
          const xName = x.Name?.toLowerCase() ?? '';
          const yName = y.Name?.toLowerCase() ?? '';
          return xName > yName ? 1 : (xName < yName ? -1 : 0);
        });
      }
      return matched;
    }
    return [];
  }

  // Return true if the macro can be created or modified.
  // Duplicate named macros are not allowed.
  isSaveAllowed(macroDefinitionDto: MacroDefinitionDto): boolean {
    if (!macroDefinitionDto || !macroDefinitionDto.Name) return false;

    if (!macroDefinitionDto.Id) {
      // creating new macro, make sure macro with the same name is not already defined
      if (this.macroDefinitions && this.macroDefinitions.filter((macro) =>
        macro.Name?.toLowerCase() === macroDefinitionDto.Name?.toLowerCase()).length > 0) {
        return false;
      }
      return true;
    }
    // Modifying an existing macro.
    // Find macro and make sure name has not been changed to an already existing macro.
    const existingMacro = this.macroDefinitions?.find((macro) =>
      macro.Name === macroDefinitionDto.Name && macro.Id !== macroDefinitionDto.Id);
    return !existingMacro;
  }

  doesMacroExist(name: string): boolean {
    if (this.macroDefinitions?.find((macro) =>
      macro.Name?.toLowerCase() === name.toLowerCase())) {
      return true;
    }
    return false;
  }

  savePersistentData(persistentData: MacroActionValueDtoExtended): MacroActionValueDtoExtended {
    const dataToSave = getFromBetween(persistentData.Value || '', '[[', ']]');
    if (!dataToSave) {
      persistentData.Value = 'ptext requires that text to be saved is placed between "[[...]]"';
      persistentData.Action = MacroActionValueType.ShowWarning;
    } else {
      let s1: string | null = this.removeFromBetweenDelimiters(persistentData.Value, '[[');
      if (!s1) {
        persistentData.Value = 'Unable to find [[';
        persistentData.Action = MacroActionValueType.ShowWarning;
      } else {
        s1 = this.removeFromBetweenDelimiters(s1, ']]');
        if (!s1) {
          persistentData.Value = 'Unable to find ]]';
          persistentData.Action = MacroActionValueType.ShowWarning;
        } else {
          persistentData.FilteredValue = dataToSave;
          persistentData.Value = s1;
          persistentData.Action = MacroActionValueType.ShowValue;
        }
      }
    }
    return persistentData;
  }

  // Delete Macro with specified Id.
  deleteMacro(macroId: string, forceDeleteObs: Observable<boolean>): Observable<MacroDeleteResultDto> {
    const item = this.removeMacroFromList(macroId);
    if (!item) return EMPTY;

    this.dismissSnackBar();
    const shackbarDismissedObs = this.showMacroDeleteSnackbarWithUndo(item);

    forceDeleteObs.pipe(
      take(1),
      this.takeUntilDestroy(),
    ).subscribe(
      // Dismiss SnackBar if open on close and send delete to backend to delete macro
      () => {
        this.dismissSnackBar();
      },
      (error) => { throwError(error); },
    );

    return shackbarDismissedObs.pipe(
      filter(() => !!this.activeSnackBarItem),
      switchMap(() => {
        return this.onMacroDelete(item);
      }),
      tap((result) => {
        if (result.Errors.length > 0 && this.activeSnackBarItem) {
          console.log('MacroService deleteMacro error ' + result.Errors[0]);
          this.addMacroDefinitionDto(this.activeSnackBarItem);
        }
      }),
      catchError((error) => {
        this.addMacroDefinitionDto(item);
        throw error;
      })
    );
  }

  // Dismiss snackbar if open
  private dismissSnackBar(): void {
    if (this.snackBarRef) {
      this.snackBarRef.dismiss();
    }
    this.activeSnackBarItem = null;
  }

  private doMacroBackendProcessing(item: MacroDefinitionDto, macroConfigDto: MacroConfigDto): Observable<MacroExecutionResultDto> {
    const visitId = this.getVisitId();

    if (visitId) {
      const param = new MacroExecutionDto();

      if (item.Id) {
        param.MacrosToExecute = [item.Id];
      }
      param.SelectionIds = item.SelectionIds;
      param.DxId = macroConfigDto.macroInterfaces.dxId;

      return this.macroRepository.executeMacroBackend(this.getPatientId(), visitId, param);
    }
    else {
      const result = new MacroExecutionResultDto();
      result.Errors = ['Visit not provided'];
      return of(result);
    }
  }

  private processMacroBackendResult(result: MacroExecutionResultDto) {
    if (result.Errors.length > 0) {
      this.apNotificationService.errorWList(result.Errors, 'Errors');
    }

    this.processDiagnosesAdded(result);
    this.processProcedureAdded(result);
    this.processGoalAdded(result);
    this.processMedicationAdded(result);

    if (result.PreviouslyAddedMessages.length > 0) {
      this.apNotificationService.successWList(result.PreviouslyAddedMessages, 'Previously Added Items');
    }
  }

  private processDiagnosesAdded(result: MacroExecutionResultDto) {
    if (result.DxsAddedMessages.length > 0) {
      this.apNotificationService.successWList(result.DxsAddedMessages, 'Diagnoses Added');

      if (this.eventNotifications) {
        this.eventNotifications.notifyDxChanged();
      }
    }

    const visitId = this.getVisitId();
    if (visitId && result.ShouldRunDrugScreening) {
      this.drugScreeningService?.runDrugScreening(this.getPatientId(), visitId, true,
        'This patient has a drug screening alert for a diagnosis or medication');
    }
  }

  private processProcedureAdded(result: MacroExecutionResultDto) {
    const msgs = [...result.SPsAddedMessages, ...result.SOsAddedMessages];
    if (msgs.length > 0) {
      this.apNotificationService.successWList(msgs, 'Procedures Added');
      if (this.eventNotifications) {
        this.eventNotifications.notifyProcedureChanged();
      }
    }

    if (result.HasVaccineChanges) {
      if (this.eventNotifications) {
        this.eventNotifications.notifyVaccineChanged();
      }
    }
  }

  private processGoalAdded(result: MacroExecutionResultDto) {
    if (result.GoalsAddedMessages.length > 0) {
      this.apNotificationService.successWList(result.GoalsAddedMessages, 'Goal Added');
      if (this.eventNotifications) {
        this.eventNotifications.notifyCarePlanChanged();
      }
    }
  }

  private processMedicationAdded(result: MacroExecutionResultDto) {
    if (result.MedicationsAddedMessages.length > 0) {
      this.apNotificationService.successWList(result.MedicationsAddedMessages, 'Medication Added');

      if (this.eventNotifications) {
        this.eventNotifications.notifyMedicationAdded();
      }
    }
    // Drug screening is handled once, above, in 'processDiagnosesAdded'.
  }

  private getValueLocally(item: MacroDefinitionDto, macroInterfaces: MacroInterfaces, editorName: string)
    : Observable<MacroActionValueDtoExtended> {
    if (item.DataContext === MacroContextEnum.Patient) {
      return of(this.getPatientValue(item.DataPropertyPath, item.Name));
    }
    if (item.DataContext === MacroContextEnum.Visit) {
      return of(this.getVisitValue(item.DataPropertyPath, item.Name));
    }
    if (item.Name === 'today') {
      return of(this.getTodayValue());
    }
    if (item.Name === 'same') {
      return of(this.getSameValue(macroInterfaces));
    }
    if (item.Name === 'rtext') {
      return of(this.getRtextValue(macroInterfaces));
    }
    if (item.Name === 'ptext') {
      return of(this.getPtextValue(macroInterfaces));
    }
    if (item.Name === 'samenote') {
      return of(this.getSameNoteValue(macroInterfaces));
    }
    if (item.Name === 'date'){
      return this.getDateSelectorObservable(editorName);
    }

    const result: MacroActionValueDtoExtended = {
      Action: MacroActionValueType.ShowValue,
      Value: item.Text,
    };
    return of(result);
  }

  private getPatientValue(dataPropertyPath: string, macroName: string | null): MacroActionValueDtoExtended {
    const result: MacroActionValueDtoExtended = {
      Value: this.macroDataService.getPatientProperty(dataPropertyPath),
      Action: MacroActionValueType.ShowValue,
    };
    this.showWarningIfNoData(result, macroName);
    return result;
  }

  private getVisitValue(dataPropertyPath: string, macroName: string | null): MacroActionValueDtoExtended {
    const result: MacroActionValueDtoExtended = {
      Value: this.macroDataService.getCurrentVisitProperty(dataPropertyPath),
      Action: MacroActionValueType.ShowValue,
    };
    this.showWarningIfNoData(result, macroName);
    return result;
  }

  private getTodayValue(): MacroActionValueDtoExtended {
    const result: MacroActionValueDtoExtended = {
      Value: moment().format('M/D/YYYY'),
      Action: MacroActionValueType.ShowValue,
    };
    return result;
  }

  private getSameValue(macroInterfaces: MacroInterfaces): MacroActionValueDtoExtended {
    let result: MacroActionValueDtoExtended;
    const sametext = macroInterfaces.getSame();
    if (sametext) {
      result = {
        Value: sametext,
        Action: MacroActionValueType.ShowValue,
      };
    } else {
      result = {
        Value: 'Previous value not found',
        Action: MacroActionValueType.ShowWarning,
      };
    }
    return result;
  }

  private getRtextValue(macroInterfaces: MacroInterfaces): MacroActionValueDtoExtended {
    let result: MacroActionValueDtoExtended;
    let rtext: string | null;
    rtext = macroInterfaces.getRtext();
    if (rtext) {
      result = {
        Value: rtext,
        Action: MacroActionValueType.ShowValue,
      };
    } else {
      result = {
        Value: 'No persistent text found',
        Action: MacroActionValueType.ShowWarning,
      };
    }
    return result;
  }

  private getPtextValue(macroInterfaces: MacroInterfaces): MacroActionValueDtoExtended {
    const result: MacroActionValueDtoExtended = {
      Action: MacroActionValueType.PersistentData,
      MacroInterfaces: macroInterfaces,
      Value: null,
    };
    return result;
  }

  private getSameNoteValue(macroInterfaces: MacroInterfaces) {
    let result: MacroActionValueDtoExtended;
    const samenotetext = macroInterfaces.getSameNote();
    if (samenotetext) {
      result = {
        Value: samenotetext,
        Action: MacroActionValueType.ShowValue,
      };
    } else {
      result = {
        Value: 'Previous value not found',
        Action: MacroActionValueType.ShowWarning,
      };
    }
    return result;
  }

  private getValueFromServer(item: MacroDefinitionDto, editorName: string): Promise<MacroActionValueDtoExtended> {
    const promise = new Promise<MacroActionValueDtoExtended>(async (resolve) => {
      const value = await this.macroRepository.getMacroValue(this.getPatientId(),
        this.getVisitId(), item.Name,
        item.Id, item.SelectionIds, editorName).toPromise();

      if (value) {
        return resolve(value);
      }
    });
    return promise;
  }

  private sortMacroDefinitions(): void {
    this.macroDefinitions = this.macroDefinitions?.sort((x, y) => {
      const xName = x.Name?.toLowerCase() ?? '';
      const yName = y.Name?.toLowerCase() ?? '';
      return xName > yName ? 1 : (xName < yName ? -1 : 0);
    });
  }

  private onLoadError() {
    //
  }

  private removeFromBetweenDelimiters(s: string | null, sub1: string): string | null {
    if (!s) return null;
    if (s.indexOf(sub1) < 0) return null;
    s = s.replace(sub1, '');
    return s;
  }

  private reset() {
    this.macroDefinitions = undefined;
  }

  private processPatientInfoChanged(changes: PatientInfoChangedEventArgs) {
    if (find(changes.itemsUpdated, (change) => change.item === PatientInfoItem.PatientStory)) {
      this.macroDataService.reloadPatientData(changes.patientId);
    }
  }

  private showWarningIfNoData(result: MacroActionValueDtoExtended, macroName: string | null) {
    if (!result.Value) {
      result.Value = 'No data defined for macro ' + macroName;
      result.Action = MacroActionValueType.ShowWarning;
    }
  }

  private getValue(item: MacroDefinitionDto, macroConfigDto: MacroConfigDto): Observable<MacroActionValueDtoExtended> {
    if (item.DataSource === MacroDataSourceEnum.Both || item.DataSource === MacroDataSourceEnum.Client) {
      return this.getValueLocally(item, macroConfigDto.macroInterfaces, macroConfigDto.editorName);
    }

    return from(this.getValueFromServer(item, macroConfigDto.editorName));
  }

  // Send request to delete macro to back-end
  private onMacroDelete(macroDefinitionDto: MacroDefinitionDto): Observable<MacroDeleteResultDto> {
    if (!this.activeSnackBarItem || !macroDefinitionDto || !macroDefinitionDto.Id) { return EMPTY; }
    return this.macroRepository.deleteMacro(this.getPatientId(), macroDefinitionDto.Id);
  }

  private showMacroDeleteSnackbarWithUndo(macroDefinitionDto: MacroDefinitionDto): Observable<MatSnackBarDismiss> {
    this.activeSnackBarItem = macroDefinitionDto;

    this.snackBarRef =
      this.apSnackBarService.showUndoSnackBar(`${macroDefinitionDto.Name} Selection Deleted`);

    // Subscribe to snackBar action (Undo button pressed)
    this.snackBarRef.onAction().pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(
      () => {
        this.undoMacroDelete();
      },
      (error: unknown) => {
        this.apNotificationService.displayStandardError();
        throw error;
      },
    );

    // Return observable that is notified when the snackbar is finished closing
    return this.snackBarRef.afterDismissed();
  }

  private undoMacroDelete(): void {
    // do undo here
    if (!this.activeSnackBarItem) return;
    this.addMacroDefinitionDto(this.activeSnackBarItem);

    this.activeSnackBarItem = null;
    this.dismissSnackBar();
  }

  // Remove macro from macroDefinitions and return macro if foun!t
  private removeMacroFromList(id: string): MacroDefinitionDto | null {
    if (!this.macroDefinitions) return null;
    let removedMacro: MacroDefinitionDto;
    const index = this.findMacroIndexInMacroDefinitions(id);
    removedMacro = this.macroDefinitions[index];
    if (removedMacro) {
      this.removeMacroFromMacroDefinitions(index);
    }
    return removedMacro;
  }

  private findMacroIndexInMacroDefinitions(id: string): number {
    if (!this.macroDefinitions) return -1;
    return this.macroDefinitions.findIndex((m) => m.Id === id);
  }

  private removeMacroFromMacroDefinitions(index: number): void {
    if (!this.macroDefinitions) return;
    this.macroDefinitions.splice(index, 1);
    this.onMacroListHasChanged();
  }

  private getDateSelectorObservable(editorName: string): Observable<MacroActionValueDtoExtended> {
    return new Observable((observer) => {
      // Force code to run in Angular Zone.
      // At this point if called from the AutoCompleter class, code
      // is running outside of Angular. ngZone.run will run the code
      // to reenter the Angular zone.

      this.ngZone.run(() => {
        this.macroDatePickerModalService.openModal(editorName).then(
          async (data) => {
            const v: MacroActionValueDtoExtended = {
              Action: MacroActionValueType.ShowValue,
              Value: moment(data).format('M/D/YYYY'),
            };
            observer.next(v);
            observer.complete();
          },
          () => {
            const v: MacroActionValueDtoExtended = {
              Action: MacroActionValueType.ShowValue,
              Value: '',
            };
            observer.next(v);
            observer.complete();
          },
        );
      });
    });
  }

  onMacroListHasChanged(): void {
    this.macroListChangedSubject$.next(true);
  }

  isInitRequired(patientId: string, visitId: string | null = null): boolean {
    if (this.macroDataService.isInitRequired(patientId, visitId)) return true;
    return this.macroDefinitions ? false : true;
  }
}
